// @ts-check
// Formulário da mensagem do bot (contrato de bots, seções 4.1, 11.3 e 22.2): o texto com o contador pelo limite da Meta
// (4.096, 1.024 com botões), a mídia, e as opções: sem opções, até 3 botões, lista de até 10 linhas (com o texto do
// botão, cabeçalho, rodapé e descrição de cada linha, cada um com o contador) ou as opções do cadastro (unidades,
// segmentos, cursos, períodos). "Falar com uma pessoa" conta no limite. Embaixo, a prévia no estilo da conversa.
import { html } from 'htm/preact';
import { Button } from '../../../ui/button.js';
import { Toggle } from '../../../ui/feedback.js';
import { Icon } from '../../../ui/icon.js';
import { SegmentedControl } from '../../../ui/segmented-control.js';
import {
  BOT_MESSAGE_LIMITS, bodyLimitOf, buildBotMessagePreview, characterCounter, optionMaximumOf, optionTitleLimitOf,
} from '../../../lib/bot-message-limits.js';
import { renderPreviewText } from '../../../lib/bot-test-simulator.js';
import { BOT_OPTION_SOURCES } from '../../../../../shared/bots/bot-definition.js';
import { FlowField, FlowNotice } from '../../flows/flow-ui.js';
import { BotMessagePreview } from '../bot-message-preview.js';
import { BotNumberField, BotSaveToField, BotSelectField, BotTextField, useFieldId } from '../bot-form-fields.js';
import { botFieldProblemText, botProblemText } from '../bot-ui.js';
import { t } from '../../../i18n/index.js';

/**
 * @typedef {import('../bot-node-panel.js').BotNodeFormProps} BotNodeFormProps
 * @typedef {'NONE' | 'BUTTONS' | 'LIST' | 'DYNAMIC'} MessageMode
 */

/** Modo das opções da mensagem. @param {Record<string, any>} config @returns {MessageMode} */
export function messageModeOf(config) {
  const options = config?.options;
  if (!options?.source) return 'NONE';
  if (options.source !== 'STATIC') return 'DYNAMIC';
  return options.presentation === 'LIST' ? 'LIST' : 'BUTTONS';
}

/**
 * Configuração das opções para o modo escolhido, aproveitando o que já existe (as linhas da lista viram botões até 3).
 * @param {Record<string, any>} config @param {MessageMode} mode @param {() => string} generateId
 * @returns {Record<string, any> | null}
 */
export function optionsForMode(config, mode, generateId) {
  const current = config?.options ?? null;
  if (mode === 'NONE') return null;
  const base = {
    header: current?.header ?? null, footer: current?.footer ?? null, saveTo: current?.saveTo ?? null, addHumanOption: current?.addHumanOption ?? false,
    retries: current?.retries ?? null, timeoutMinutes: current?.timeoutMinutes ?? null, segmentVariable: current?.segmentVariable ?? null,
  };
  if (mode === 'DYNAMIC') {
    return { ...base, source: current?.source && current.source !== 'STATIC' ? current.source : 'CAMPUSES', presentation: 'LIST', listButtonLabel: current?.listButtonLabel ?? t('bots.catalog.defaults.listButton'), items: [] };
  }
  const existing = current?.source === 'STATIC' && Array.isArray(current.items) ? current.items : [];
  const presentation = mode === 'LIST' ? 'LIST' : 'BUTTONS';
  const maximum = presentation === 'BUTTONS' ? BOT_MESSAGE_LIMITS.buttons.maximum - (base.addHumanOption ? 1 : 0) : BOT_MESSAGE_LIMITS.list.maximum;
  const items = existing.length > 0
    ? existing.slice(0, Math.max(1, maximum))
    : [{ id: generateId(), title: t('bots.catalog.defaults.yes'), description: null }, { id: generateId(), title: t('bots.catalog.defaults.no'), description: null }];
  return { ...base, source: 'STATIC', presentation, listButtonLabel: presentation === 'LIST' ? current?.listButtonLabel ?? t('bots.catalog.defaults.listButton') : null, items };
}

/** @param {BotNodeFormProps} props */
export function MessageForm({ node, problems, settings, variables, institutionName, onConfig, onRequestModeChange, onAddOption, onRemoveOption }) {
  const config = node.config ?? {};
  const options = config.options ?? null;
  const mode = messageModeOf(config);
  const items = Array.isArray(options?.items) ? options.items : [];
  const maximum = options ? optionMaximumOf(options) : 0;
  const usedSlots = items.length + (options?.addHumanOption ? 1 : 0);
  const titleLimit = optionTitleLimitOf(options);
  const isList = mode === 'LIST' || mode === 'DYNAMIC';
  const preview = buildBotMessagePreview(config, {
    humanOptionLabel: settings.humanOptionLabel, renderText: (text) => renderPreviewText(text, { institutionName }),
    dynamicRows: [{ title: t(`bots.optionSources.${options?.source ?? 'CAMPUSES'}`) }, { title: '…' }], listButtonFallback: t('bots.preview.listButtonFallback'),
  });
  const interactiveProblems = problems.filter((problem) => problem.code === 'INTERACTIVE_INVALID' && problem.details?.field === 'options.items');
  /** @param {string} optionId @param {Record<string, any>} patch */
  const updateItem = (optionId, patch) => onConfig({ options: { ...options, items: items.map((item) => (item.id === optionId ? { ...item, ...patch } : item)) } });
  const mediaId = useFieldId('bot-media');
  return html`<div class="flow-node-form bot-node-form" data-testid="bot-message-form">
    <${BotTextField} label=${t('bots.forms.message.text')} value=${config.text} multiline rows=${4} maximum=${bodyLimitOf(config)} variables=${variables}
      onChange=${(text) => onConfig({ text })} error=${botFieldProblemText(problems.filter((problem) => problem.code !== 'INTERACTIVE_INVALID'), 'text')}
      testId="bot-message-text" />
    <${FlowField} label=${t('bots.forms.message.media')} htmlFor=${mediaId} hint=${config.media?.fileId ? null : t('bots.forms.message.mediaHint')}>
      <div class="bot-form-row">
        <select id=${mediaId} class="text-input" value=${config.media?.kind ?? ''} onChange=${(/** @type {Event} */ event) => {
          const kind = /** @type {HTMLSelectElement} */ (event.currentTarget).value;
          onConfig({ media: kind ? { fileId: config.media?.fileId ?? null, kind } : null });
        }}>
          <option value="">${t('bots.forms.message.noMedia')}</option>
          ${['IMAGE', 'DOCUMENT', 'AUDIO'].map((kind) => html`<option key=${kind} value=${kind}>${t(`bots.preview.media.${kind}`)}</option>`)}
        </select>
        ${config.media?.kind
          ? html`<input class="text-input" value=${config.media?.fileId ?? ''} placeholder=${t('bots.forms.message.fileId')} aria-label=${t('bots.forms.message.fileId')}
              onInput=${(/** @type {InputEvent} */ event) => onConfig({ media: { ...config.media, fileId: /** @type {HTMLInputElement} */ (event.currentTarget).value || null } })} />`
          : null}
      </div>
    <//>
    <div class="flow-field">
      <span class="flow-field-label">${t('bots.forms.message.answers')}</span>
      <${SegmentedControl} label=${t('bots.forms.message.answers')} value=${mode}
        options=${['NONE', 'BUTTONS', 'LIST', 'DYNAMIC'].map((value) => ({ value, label: t(`bots.forms.message.modes.${value}`) }))}
        onChange=${(/** @type {MessageMode} */ value) => onRequestModeChange?.(value)} />
    </div>
    ${mode === 'DYNAMIC'
      ? html`<${BotSelectField} label=${t('bots.forms.message.source')} value=${options.source} placeholder=${null}
          options=${BOT_OPTION_SOURCES.filter((source) => source !== 'STATIC').map((source) => ({ value: source, label: t(`bots.optionSources.${source}`) }))}
          onChange=${(source) => onConfig({ options: { ...options, source } })} hint=${t('bots.forms.message.sourceHint')} />
        ${options.source === 'COURSES'
          ? html`<${BotSelectField} label=${t('bots.forms.message.segmentVariable')} value=${options.segmentVariable} placeholder=${t('bots.forms.message.allSegments')}
              options=${variables.map((name) => ({ value: name, label: `bot.${name}` }))} onChange=${(segmentVariable) => onConfig({ options: { ...options, segmentVariable } })} />`
          : null}`
      : null}
    ${mode === 'BUTTONS' || mode === 'LIST'
      ? html`<div class="bot-options-editor" data-testid="bot-options-editor">
          <div class="bot-options-head">
            <span class="flow-field-label">${mode === 'BUTTONS' ? t('bots.forms.message.buttons') : t('bots.forms.message.rows')}</span>
            <span class="bot-counter" data-over=${usedSlots > maximum ? 'true' : undefined}>${t('bots.forms.message.slots', { count: usedSlots, maximum })}</span>
          </div>
          <ol class="bot-options-list">
            ${items.map((item, position) => {
              const counter = characterCounter(item.title, titleLimit);
              const itemProblem = interactiveProblems.find((problem) => problem.details?.optionId === item.id);
              return html`<li key=${item.id} class="bot-option-row">
                <span class="bot-option-number" aria-hidden="true">${position + 1}</span>
                <div class="bot-option-fields">
                  <div class="bot-option-title">
                    <input class="text-input" value=${item.title ?? ''} aria-label=${t('bots.forms.message.optionTitle', { number: position + 1 })}
                      aria-invalid=${counter.isOver || itemProblem ? 'true' : undefined} data-testid=${`bot-option-title-${position}`}
                      onInput=${(/** @type {InputEvent} */ event) => updateItem(item.id, { title: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
                    <span class="bot-counter" data-over=${counter.isOver ? 'true' : undefined}>${counter.text}</span>
                  </div>
                  ${mode === 'LIST'
                    ? html`<div class="bot-option-title">
                        <input class="text-input" value=${item.description ?? ''} placeholder=${t('bots.forms.message.optionDescription')}
                          aria-label=${t('bots.forms.message.optionDescriptionLabel', { number: position + 1 })}
                          onInput=${(/** @type {InputEvent} */ event) => updateItem(item.id, { description: /** @type {HTMLInputElement} */ (event.currentTarget).value || null })} />
                        <span class="bot-counter" data-over=${characterCounter(item.description, BOT_MESSAGE_LIMITS.list.rowDescription).isOver ? 'true' : undefined}>
                          ${characterCounter(item.description, BOT_MESSAGE_LIMITS.list.rowDescription).text}
                        </span>
                      </div>`
                    : null}
                  ${itemProblem ? html`<span class="flow-field-error" role="alert">${botProblemText(itemProblem)}</span>` : null}
                </div>
                <button type="button" class="bot-option-remove" aria-label=${t('bots.forms.message.removeOption', { title: item.title || position + 1 })}
                  title=${t('bots.forms.message.removeOption', { title: item.title || position + 1 })} disabled=${items.length <= 1}
                  onClick=${() => onRemoveOption?.(item.id)}><${Icon} name="IconTrash" size=${14} /></button>
              </li>`;
            })}
          </ol>
          ${usedSlots < maximum
            ? html`<${Button} title=${mode === 'BUTTONS' ? t('bots.forms.message.addButton') : t('bots.forms.message.addRow')} icon="IconPlus" variant="secondary" size="small"
                onClick=${() => onAddOption?.()} data-testid="bot-add-option" />`
            : html`<p class="flow-field-hint">${t('bots.forms.message.limitReached', { maximum })}</p>`}
          ${interactiveProblems.filter((problem) => !problem.details?.optionId).map((problem) => html`<p class="flow-field-error" role="alert">${botProblemText(problem)}</p>`)}
          <p class="flow-field-hint">${t('bots.forms.message.branchHint')}</p>
        </div>`
      : null}
    ${mode !== 'NONE'
      ? html`<label class="flow-checkbox-row bot-toggle-row">
          <${Toggle} value=${options.addHumanOption === true} ariaLabel=${t('bots.forms.message.addHumanOption', { label: settings.humanOptionLabel })}
            disabled=${!options.addHumanOption && mode !== 'DYNAMIC' && usedSlots >= maximum}
            onChange=${(/** @type {boolean} */ addHumanOption) => onConfig({ options: { ...options, addHumanOption } })} />
          <span>${t('bots.forms.message.addHumanOption', { label: settings.humanOptionLabel })}</span>
        </label>
        ${isList
          ? html`<${BotTextField} label=${t('bots.forms.message.listButton')} value=${options.listButtonLabel} maximum=${BOT_MESSAGE_LIMITS.list.button}
              onChange=${(listButtonLabel) => onConfig({ options: { ...options, listButtonLabel } })} error=${botFieldProblemText(problems.filter((problem) => problem.code === 'NODE_INCOMPLETE'), 'options.listButtonLabel')} />`
          : null}
        <details class="bot-form-details">
          <summary>${t('bots.forms.message.more')}</summary>
          <${BotTextField} label=${t('bots.forms.message.header')} value=${options.header} maximum=${isList ? BOT_MESSAGE_LIMITS.list.header : BOT_MESSAGE_LIMITS.buttons.header}
            onChange=${(header) => onConfig({ options: { ...options, header: header || null } })} />
          <${BotTextField} label=${t('bots.forms.message.footer')} value=${options.footer} maximum=${isList ? BOT_MESSAGE_LIMITS.list.footer : BOT_MESSAGE_LIMITS.buttons.footer}
            onChange=${(footer) => onConfig({ options: { ...options, footer: footer || null } })} />
          <${BotSaveToField} value=${options.saveTo} allowField=${mode === 'DYNAMIC'}
            onChange=${(saveTo) => onConfig({ options: { ...options, saveTo: saveTo.variable ? saveTo : null } })}
            variableError=${botFieldProblemText(problems, 'options.saveTo.variable')} />
          <div class="bot-form-row">
            <${BotNumberField} label=${t('bots.forms.retries')} value=${options.retries} minimum=${0} maximum=${3} placeholder=${String(settings.retries)}
              onChange=${(retries) => onConfig({ options: { ...options, retries } })} />
            <${BotNumberField} label=${t('bots.forms.timeoutMinutes')} value=${options.timeoutMinutes} minimum=${5} maximum=${1380} placeholder=${String(settings.questionTimeoutMinutes)}
              onChange=${(timeoutMinutes) => onConfig({ options: { ...options, timeoutMinutes } })} error=${botFieldProblemText(problems, 'timeoutMinutes')} />
          </div>
        </details>`
      : null}
    ${mode === 'NONE' && config.media?.kind === 'AUDIO' ? html`<${FlowNotice}>${t('bots.forms.message.audioNotice')}<//>` : null}
    <div class="bot-preview-box">
      <span class="flow-field-label">${t('bots.preview.title')}</span>
      <div class="bot-chat-surface"><${BotMessagePreview} model=${preview} /></div>
    </div>
  </div>`;
}
